import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  FileText, Search, Eye, Store, Truck, RefreshCw, Printer, 
  CheckCircle, Loader2, Package, Send, ClipboardList,
  ArrowRight, IndianRupee, MapPin, Calendar
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import api from '@/services/api';

const STATUS_CONFIG = {
  approved: { label: 'Pending Dispatch', color: 'bg-blue-100 text-blue-700', icon: ClipboardList },
  dispatched: { label: 'Dispatched', color: 'bg-amber-100 text-amber-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  received: { label: 'Received', color: 'bg-green-100 text-green-700', icon: Package },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

const StockTransferPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('pending');
  const [transferOrders, setTransferOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Dialog states
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [showDispatchNoteDialog, setShowDispatchNoteDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Dispatch form
  const [dispatchForm, setDispatchForm] = useState({
    vehicle_number: '',
    driver_name: '',
    driver_phone: '',
    transporter_name: '',
    lr_number: '',
    expected_delivery_date: '',
    dispatch_notes: ''
  });
  
  // Dispatch note data
  const [dispatchNoteData, setDispatchNoteData] = useState(null);
  const printRef = useRef();

  const fetchTransferOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        source_store_id: user?.store_id || user?.entity_id,
        page_size: 100 
      };
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      
      const response = await api.get('/store/stock-transfer/transfer-orders', { params });
      setTransferOrders(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Error fetching transfer orders:', error);
      toast({ title: 'Error', description: 'Failed to fetch transfer orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.store_id, user?.entity_id, statusFilter, searchQuery, toast]);

  useEffect(() => {
    fetchTransferOrders();
  }, [fetchTransferOrders]);

  // View order details
  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsDialog(true);
  };

  // Open dispatch dialog
  const openDispatchDialog = (order) => {
    setSelectedOrder(order);
    setDispatchForm({
      vehicle_number: '',
      driver_name: '',
      driver_phone: '',
      transporter_name: '',
      lr_number: '',
      expected_delivery_date: '',
      dispatch_notes: ''
    });
    setShowDispatchDialog(true);
  };

  // Dispatch order
  const handleDispatch = async () => {
    if (!dispatchForm.vehicle_number) {
      toast({ title: 'Error', description: 'Vehicle number is required', variant: 'destructive' });
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/store/stock-transfer/transfer-orders/${selectedOrder.id}/dispatch`, dispatchForm);
      toast({ title: 'Success', description: 'Stock transfer dispatched successfully' });
      setShowDispatchDialog(false);
      fetchTransferOrders();
    } catch (error) {
      console.error('Error dispatching:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to dispatch', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  // View/Print Dispatch Note
  const viewDispatchNote = async (order) => {
    setSelectedOrder(order);
    try {
      const response = await api.get(`/store/stock-transfer/transfer-orders/${order.id}/dispatch-note`);
      setDispatchNoteData(response.data);
      setShowDispatchNoteDialog(true);
    } catch (error) {
      console.error('Error fetching dispatch note:', error);
      toast({ title: 'Error', description: 'Failed to fetch dispatch note', variant: 'destructive' });
    }
  };

  // Print dispatch note
  const handlePrintDispatchNote = () => {
    const printContent = printRef.current;
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Dispatch Note - ${dispatchNoteData?.dispatch_info?.dispatch_number || ''}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
              .company-name { font-size: 24px; font-weight: bold; }
              .company-address { font-size: 12px; color: #666; }
              .title { font-size: 18px; font-weight: bold; margin: 20px 0; text-align: center; }
              .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
              .info-box { border: 1px solid #ddd; padding: 10px; margin-bottom: 15px; }
              .info-box h4 { margin: 0 0 5px 0; font-size: 12px; color: #666; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background: #f5f5f5; }
              .text-right { text-align: right; }
              .footer { margin-top: 40px; display: flex; justify-content: space-between; }
              .signature { border-top: 1px solid #333; width: 150px; text-align: center; padding-top: 5px; }
              @media print { body { print-color-adjust: exact; } }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Filter orders by tab
  const getFilteredOrders = () => {
    switch (activeTab) {
      case 'pending':
        return transferOrders.filter(o => o.status === 'approved');
      case 'dispatched':
        return transferOrders.filter(o => ['dispatched', 'delivered', 'received', 'completed'].includes(o.status));
      default:
        return transferOrders;
    }
  };

  const filteredOrders = getFilteredOrders();

  return (
    <div className="p-6 space-y-6" data-testid="stock-transfer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-100 rounded-xl">
            <Truck className="w-8 h-8 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Stock Transfer</h1>
            <p className="text-gray-500">Process stock transfers to other owned stores (Same GST)</p>
          </div>
        </div>
        <Button onClick={fetchTransferOrders} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm">Pending Dispatch</p>
                <p className="text-2xl font-bold text-blue-800">
                  {transferOrders.filter(o => o.status === 'approved').length}
                </p>
              </div>
              <ClipboardList className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-600 text-sm">In Transit</p>
                <p className="text-2xl font-bold text-amber-800">
                  {transferOrders.filter(o => o.status === 'dispatched').length}
                </p>
              </div>
              <Truck className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-600 text-sm">Completed</p>
                <p className="text-2xl font-bold text-emerald-800">
                  {transferOrders.filter(o => ['delivered', 'received', 'completed'].includes(o.status)).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            <ClipboardList className="w-4 h-4" /> Pending Dispatch
          </TabsTrigger>
          <TabsTrigger value="dispatched" className="gap-2">
            <Truck className="w-4 h-4" /> Dispatched
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2">
            <Package className="w-4 h-4" /> All Orders
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Transfer Order</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                      </TableCell>
                    </TableRow>
                  ) : filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        No orders found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => {
                      const statusConfig = STATUS_CONFIG[order.status] || { label: order.status, color: 'bg-gray-100' };
                      return (
                        <TableRow key={order.id} className="hover:bg-gray-50">
                          <TableCell>
                            <div>
                              <p className="font-semibold text-gray-800">{order.transfer_number}</p>
                              {order.store_po_number && (
                                <p className="text-xs text-gray-500">PO: {order.store_po_number}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Store className="w-4 h-4 text-indigo-500" />
                              <div>
                                <p className="font-medium">{order.destination_name}</p>
                                <p className="text-xs text-gray-500">{order.destination_code}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{order.items?.length || 0} items</Badge>
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {formatDate(order.created_at)}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            <span className="flex items-center justify-end">
                              <IndianRupee className="w-3 h-3" />
                              {(order.total_amount || 0).toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewOrderDetails(order)}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              {order.status === 'approved' && (
                                <Button
                                  size="sm"
                                  className="bg-indigo-600 hover:bg-indigo-700"
                                  onClick={() => openDispatchDialog(order)}
                                >
                                  <Send className="w-3 h-3 mr-1" /> Dispatch
                                </Button>
                              )}
                              {order.dispatch_info && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => viewDispatchNote(order)}
                                >
                                  <Printer className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Order Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Transfer Order Details
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Transfer Number</p>
                  <p className="font-semibold">{selectedOrder.transfer_number}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={STATUS_CONFIG[selectedOrder.status]?.color || 'bg-gray-100'}>
                    {STATUS_CONFIG[selectedOrder.status]?.label || selectedOrder.status}
                  </Badge>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Destination Store</p>
                  <p className="font-semibold">{selectedOrder.destination_name}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Created Date</p>
                  <p className="font-semibold">{formatDate(selectedOrder.created_at)}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Items</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedOrder.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.sku}</p>
                        </TableCell>
                        <TableCell className="text-right">{item.approved_qty || item.quantity}</TableCell>
                        <TableCell className="text-right">₹{item.transfer_price || item.unit_price}</TableCell>
                        <TableCell className="text-right font-semibold">
                          ₹{((item.approved_qty || item.quantity) * (item.transfer_price || item.unit_price)).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dispatch Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={setShowDispatchDialog}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-indigo-600" />
              Dispatch Stock Transfer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-indigo-50 rounded-lg">
              <p className="text-sm text-indigo-600">
                Dispatching to: <span className="font-semibold">{selectedOrder?.destination_name}</span>
              </p>
              <p className="text-xs text-indigo-500 mt-1">
                Stock will be deducted immediately from your store
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vehicle Number *</Label>
                <Input
                  value={dispatchForm.vehicle_number}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, vehicle_number: e.target.value.toUpperCase() })}
                  placeholder="TN01AB1234"
                />
              </div>
              <div>
                <Label>Driver Name</Label>
                <Input
                  value={dispatchForm.driver_name}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, driver_name: e.target.value })}
                  placeholder="Driver name"
                />
              </div>
              <div>
                <Label>Driver Phone</Label>
                <Input
                  value={dispatchForm.driver_phone}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, driver_phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <Label>Transporter</Label>
                <Input
                  value={dispatchForm.transporter_name}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, transporter_name: e.target.value })}
                  placeholder="Transporter name"
                />
              </div>
              <div>
                <Label>LR Number</Label>
                <Input
                  value={dispatchForm.lr_number}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, lr_number: e.target.value })}
                  placeholder="LR/GR number"
                />
              </div>
              <div className="col-span-2">
                <Label>Expected Delivery Date</Label>
                <Input
                  type="date"
                  value={dispatchForm.expected_delivery_date}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, expected_delivery_date: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Dispatch Notes</Label>
                <Textarea
                  value={dispatchForm.dispatch_notes}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, dispatch_notes: e.target.value })}
                  placeholder="Any special instructions..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDispatchDialog(false)}>Cancel</Button>
            <Button
              onClick={handleDispatch}
              disabled={processing || !dispatchForm.vehicle_number}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Dispatch Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch Note Print Dialog */}
      <Dialog open={showDispatchNoteDialog} onOpenChange={setShowDispatchNoteDialog}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Dispatch Note
              </span>
              <Button onClick={handlePrintDispatchNote} className="gap-2">
                <Printer className="w-4 h-4" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {dispatchNoteData && (
            <div ref={printRef} className="p-6 bg-white">
              {/* Letterhead */}
              <div className="header text-center border-b-2 border-gray-800 pb-4 mb-6">
                <h1 className="company-name text-2xl font-bold">
                  {dispatchNoteData.source_store?.name || dispatchNoteData.business?.business_name}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {dispatchNoteData.source_store?.address?.address_line1}, {dispatchNoteData.source_store?.address?.city}
                </p>
                <p className="text-sm text-gray-600">
                  GSTIN: {dispatchNoteData.source_store?.gstin || dispatchNoteData.business?.gstin}
                </p>
                <p className="text-sm text-gray-600">
                  Phone: {dispatchNoteData.source_store?.phone || dispatchNoteData.business?.phone}
                </p>
              </div>

              <h2 className="text-xl font-bold text-center mb-6">DISPATCH NOTE</h2>

              {/* Document Info */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="border p-3 rounded">
                  <h4 className="text-xs text-gray-500 mb-1">Dispatch Note No.</h4>
                  <p className="font-bold">{dispatchNoteData.dispatch_info?.dispatch_number}</p>
                  <h4 className="text-xs text-gray-500 mb-1 mt-2">Transfer Order</h4>
                  <p className="font-semibold">{dispatchNoteData.order?.transfer_number}</p>
                  <h4 className="text-xs text-gray-500 mb-1 mt-2">Dispatch Date</h4>
                  <p>{formatDate(dispatchNoteData.dispatch_info?.dispatched_at)}</p>
                </div>
                <div className="border p-3 rounded">
                  <h4 className="text-xs text-gray-500 mb-1">Dispatch To</h4>
                  <p className="font-bold">{dispatchNoteData.destination_store?.name}</p>
                  <p className="text-sm">{dispatchNoteData.destination_store?.address?.address_line1}</p>
                  <p className="text-sm">{dispatchNoteData.destination_store?.address?.city}, {dispatchNoteData.destination_store?.address?.state}</p>
                  <p className="text-sm">GSTIN: {dispatchNoteData.destination_store?.gstin}</p>
                </div>
              </div>

              {/* Transport Details */}
              <div className="border p-3 rounded mb-6">
                <h4 className="font-semibold mb-2">Transport Details</h4>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Vehicle No:</span>
                    <span className="font-semibold ml-2">{dispatchNoteData.dispatch_info?.vehicle_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Driver:</span>
                    <span className="font-semibold ml-2">{dispatchNoteData.dispatch_info?.driver_name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Driver Phone:</span>
                    <span className="font-semibold ml-2">{dispatchNoteData.dispatch_info?.driver_phone || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">LR No:</span>
                    <span className="font-semibold ml-2">{dispatchNoteData.dispatch_info?.lr_number || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">S.No</th>
                    <th className="border p-2 text-left">Product</th>
                    <th className="border p-2 text-left">HSN</th>
                    <th className="border p-2 text-right">MRP</th>
                    <th className="border p-2 text-right">Qty</th>
                    <th className="border p-2 text-left">UOM</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatchNoteData.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="border p-2">{idx + 1}</td>
                      <td className="border p-2">
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-xs text-gray-500">{item.sku}</p>
                      </td>
                      <td className="border p-2">{item.hsn_code || '-'}</td>
                      <td className="border p-2 text-right">₹{item.mrp?.toLocaleString() || '-'}</td>
                      <td className="border p-2 text-right font-semibold">{item.approved_qty || item.quantity}</td>
                      <td className="border p-2">{item.uom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Footer */}
              <div className="grid grid-cols-3 gap-8 mt-12">
                <div className="text-center">
                  <div className="border-t border-gray-400 pt-2 mt-8">Prepared By</div>
                </div>
                <div className="text-center">
                  <div className="border-t border-gray-400 pt-2 mt-8">Checked By</div>
                </div>
                <div className="text-center">
                  <div className="border-t border-gray-400 pt-2 mt-8">Received By</div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
};

export default StockTransferPage;
